'use memo';

import { Tabs } from 'types/Tabs';
import ListingInfoPanel from './ListingInfoPanel';
import ContractRequestInfoPanel from './ContractRequest/ContractRequestInfoPanel';
import ContractSubmissionInfoPanel from './ContractSubmission/ContractSubmissionInfoPanel';
import { NswContractStage } from '@urbanx/agx-ui-components';
import {
  CampaignDetailModel,
  CampaignDetailModelState,
  ContractStatuses,
  InProgressFormModelFormType,
} from '@urbanx/agx.campaigns.client';
import { useNewSelectedContract } from 'hooks/useNewSelectedContract';
import type { MarketingPackageProjection } from '@urbanx/agx.marketing.client';

interface ListingsPanelProps {
  campaign: CampaignDetailModel;
  currentTab: Tabs;
  mobile?: boolean;
  selectedMarketingPackage?: MarketingPackageProjection;
  setSelectedMarketingPackage: (
    marketingPackage: MarketingPackageProjection | undefined
  ) => void;
}

const ListingsPanel = ({
  campaign,
  currentTab,
  mobile = false,
  selectedMarketingPackage,
  setSelectedMarketingPackage,
}: ListingsPanelProps) => {
  const selectedContract = useNewSelectedContract();
  if (selectedContract == null) {
    return (
      <ListingInfoPanel
        campaign={campaign}
        currentTab={currentTab}
        mobile={mobile}
        selectedMarketingPackage={selectedMarketingPackage}
        setSelectedMarketingPackage={setSelectedMarketingPackage}
      />
    );
  }

  const selectedContractStage = selectedContract.contractStage as
    | string
    | undefined;
  const isNswPendingContract =
    campaign.state === CampaignDetailModelState.NSW &&
    (selectedContractStage === ContractStatuses.PendingSubmission ||
      selectedContractStage === NswContractStage.PendingSubmission ||
      selectedContractStage === ContractStatuses.PendingCompliance ||
      selectedContractStage === NswContractStage.PendingCompliance);

  if (isNswPendingContract) {
    return (
      <ContractSubmissionInfoPanel
        state={campaign.state ?? CampaignDetailModelState.QLD}
        contract={selectedContract}
        currentTab={currentTab}
        mobile={mobile}
      />
    );
  }

  switch (
    selectedContract.inProgressForm?.formType ??
    InProgressFormModelFormType.ContractSubmission
  ) {
    case InProgressFormModelFormType.ContractRequest:
    case InProgressFormModelFormType.ContractExchange:
      return (
        <ContractRequestInfoPanel
          campaign={campaign}
          contract={selectedContract}
          currentTab={currentTab}
          mobile={mobile}
        />
      );
    case InProgressFormModelFormType.ContractSubmission:
    default:
      return (
        <ContractSubmissionInfoPanel
          state={campaign.state ?? CampaignDetailModelState.QLD}
          contract={selectedContract}
          currentTab={currentTab}
          mobile={mobile}
        />
      );
  }
};

export default ListingsPanel;
